import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { register } from '../api/authService';
import { useAuth } from '../auth/AuthContext';

function SignupPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const validate = () => {
    const nextErrors = {};
    const name = form.name.trim();
    const email = form.email.trim();
    const password = form.password;
    const confirmPassword = form.confirmPassword;

    if (!name) {
      nextErrors.name = 'Name is required.';
    } else if (name.length < 2) {
      nextErrors.name = 'Name must be at least 2 characters.';
    }

    if (!email) {
      nextErrors.email = 'Email is required.';
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!password) {
      nextErrors.password = 'Password is required.';
    } else if (password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters.';
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = 'Please confirm your password.';
    } else if (confirmPassword !== password) {
      nextErrors.confirmPassword = 'Passwords do not match.';
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
    setSuccess('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      };

      const response = await register(payload);
      const message = response?.message || 'Account created successfully.';
      setSuccess(`${message} Redirecting to sign in...`);

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1200);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Sign up failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap signup-page">
      <div className="signup-shell">
        <section className="signup-brand-panel" aria-hidden="true">
          <p className="login-brand-kicker">Get Started With PRNB</p>
          <h1>Sign up and start managing leads with confidence.</h1>
          <p className="login-brand-copy">
            Create your account to explore queue workflows, assignment rules, and live pipeline visibility in one modern workspace.
          </p>
          <ul className="login-brand-points">
            <li>Create and route leads faster</li>
            <li>Track assignment ownership clearly</li>
            <li>Monitor SLA health in real time</li>
          </ul>
        </section>

        <form className="auth-card login-card signup-card" onSubmit={onSubmit}>
          <h2>Create account</h2>
          <p className="auth-subtitle">Set up your LMS profile in less than a minute.</p>

        <label className="auth-label" htmlFor="signup-name">
          Full Name
          <input
            id="signup-name"
            name="name"
            type="text"
            value={form.name}
            onChange={onChange}
            autoComplete="name"
            required
          />
        </label>
        {formErrors.name ? <p className="error-text">{formErrors.name}</p> : null}

        <label className="auth-label" htmlFor="signup-email">
          Email
          <input
            id="signup-email"
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            autoComplete="email"
            required
          />
        </label>
        {formErrors.email ? <p className="error-text">{formErrors.email}</p> : null}

        <label className="auth-label" htmlFor="signup-password">
          Password
          <div className="password-field">
            <input
              id="signup-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={onChange}
              autoComplete="new-password"
              required
            />
            <button
              className="toggle-password-btn"
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </label>
        {formErrors.password ? <p className="error-text">{formErrors.password}</p> : null}

        <label className="auth-label" htmlFor="signup-confirm-password">
          Confirm Password
          <input
            id="signup-confirm-password"
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={form.confirmPassword}
            onChange={onChange}
            autoComplete="new-password"
            required
          />
        </label>
        {formErrors.confirmPassword ? <p className="error-text">{formErrors.confirmPassword}</p> : null}

        {error ? <p className="error-text">{error}</p> : null}
        {success ? <p className="success-text">{success}</p> : null}

          <button type="submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Create account'}
          </button>

          <p className="auth-footer-text">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default SignupPage;
